import { isDrivenAxis } from '@agavi/blocks';
import { ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnswerRefScope } from './answer-ref-context';
import { useBlockIdentity } from './fired-states';
import { FlowPickerMenu } from './flow-picker-menu';

import { ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { IconButton } from '@/components/ui/icon-button';
import { useCapabilities } from '@/hooks/use-capabilities';

/**
 * Which version of a question is being edited, and the controls that add or remove one.
 *
 * ## It lives in the QUESTION heading, on purpose
 *
 * A bank is a niche feature. Given its own row above the question it was the first thing a
 * teacher's eye landed on, which is the wrong weight for something most blocks will never use.
 * In the heading row it is available without being advertised — the same place a section's own
 * furniture belongs, beside a label that was going to be there anyway.
 *
 * ## Built from the branch carousel's row
 *
 * Same `IconButton size='sm'` pair around the same `text-xs font-medium tabular-nums` label, and
 * the same armed-delete contract (`variant='destructive'`, `isArmed`, disarming on pointer-leave,
 * blur and Escape). A teacher meets both pagers on one block, and two invented separately would
 * read as two unrelated widgets.
 *
 * It borrows the carousel's best trick too: on the LAST step the right chevron becomes a `+`, so
 * "go forward" and "make another" are one control rather than a nav pair plus a stray button.
 *
 * What differs is CONTENT, not components. The branch bar carries a condition ("<0") because it
 * answers "when the variable is what?"; this carries only an index, because a version is an
 * independent draw with no condition to show.
 *
 * ## Before there is a bank
 *
 * One quiet `+`. No position label, no delete, nothing to read — the control is there for the
 * teacher who wants it and silent for everyone else.
 *
 * ## Browsing, without authoring
 *
 * Omit `onAdd` and `onRemove` and what is left is a plain pager: chevrons and a position, no `+`
 * on the last step and no delete. That is what the TEST panel needs — a teacher checking the
 * bank is stepping through versions, not building them, and a trash can on the test surface is
 * one slip away from losing an authored variant while merely looking at it.
 *
 * It is the same component rather than a second small pager on purpose. The position label is the
 * thing most likely to drift, and the editor heading and the test panel sitting a few inches
 * apart must never disagree about which version is on screen — they already did once, before
 * `useVersionInForce`.
 *
 * ## A PROJECTED axis pages and nothing else
 *
 * Versions driven by an earlier block's answer are the driver's rows, in the driver's order —
 * the teacher no more adds one than they add a scenario row, and `syncDrivenVersions` would
 * strand anything they did add. So `onAdd` and `onRemove` are ignored when the versions carry
 * a `drivenRowId`, which drops this to the browse-only pager the scenario followers already
 * use.
 *
 * READ OFF THE VERSIONS, not passed in. There are twenty-two mounts of this component and a
 * prop would be twenty-two places to remember it, which is the drift that put MCQ and Dropdown
 * a commit behind on the link menu. It also keeps holding in the one state a prop would most
 * likely miss: a block that lost its driver on paste still carries the row ids, and must not
 * quietly become a hand-drawn bank while the teacher decides what to do with it.
 */
export function VersionSelector({ versions, activeId, onSelect, onAdd: onAddRaw, onRemove: onRemoveRaw, onArmedChange, noun = 'version', showNoun = false }: {
  versions: { id: string; drivenRowId?: string }[];
  activeId: string | undefined;
  onSelect: (id: string) => void;
  /**
   * WHAT this pager is paging — a property of the block, not of the slot.
   *
   * An input block pages VERSIONS (many stems, one answer setup each); a display block's bank is
   * the scenarios other blocks follow, and the two were deliberately kept distinct (§2.2 of
   * `docs/linked-scenarios.md`). It drives the aria-labels as well as the visible position, so a
   * scenario pager never announces "Next version" — which would name the other feature.
   */
  noun?: 'version' | 'scenario';
  /**
   * WHETHER to say it, which IS a property of the slot — and OFF by default.
   *
   * Every block editor puts this pager in a heading that already reads QUESTION, so "Version 1
   * of 4" spent its longest word repeating the label beside it. That was affordable until the
   * scenario link joined the same row, at which point the noun was the reason the two controls
   * would not fit.
   *
   * It defaults off rather than being passed `false` twelve times, because twelve copies of a
   * prop is how the armed-delete ring drifted. The two slots that genuinely need a noun — a
   * display block's CARD, and the test bar's resolution row, neither of which has a label
   * beside it — ask for it.
   */
  showNoun?: boolean;
  /** Omitted on a browse-only pager: the last step then just stops instead of offering a `+`. */
  onAdd?: () => void;
  /** Omitted on a browse-only pager, which removes the armed delete entirely. */
  onRemove?: (id: string) => void;
  /**
   * Reported UP because the warning that belongs with an armed delete does not fit in this
   * cluster — the heading row is `justify-between` and already caps the name field to keep the
   * trash on the panel. The owner of the surrounding layout decides where the hint goes and
   * reserves its space; this only says when to show it.
   */
  onArmedChange?: (armed: boolean) => void;
}) {
  const { t } = useTranslation();
  /* The projection owns the list; see the note above. */
  const projected = isDrivenAxis(versions);
  /*
   * A BANK THIS TEACHER HAS NOT TURNED ON drops to the same browse-only pager.
   *
   * Read here rather than passed in, for the reason the driven axis is: twenty-two mounts is
   * twenty-two places to remember a prop, and this one would be missed in exactly the slot that
   * matters least often and hurts most — a block that already HAS a bank.
   *
   * Capability-gated, so it hides the authoring affordance and nothing else. An existing bank
   * still pages, still renders and still runs for students; a teacher who has not met versions
   * simply has no `+` and no trash. That is the browse-only mode the test panel already uses, so
   * the gate costs no new state and reaches a path that is already covered.
   *
   * A scenario bank asks for `scenarios`, which requires `questionVersions` in the graph — so a
   * display block's pager cannot offer authoring that an input block's would refuse.
   */
  const { has } = useCapabilities();
  const mayAuthor = has(noun === 'scenario' ? 'scenarios' : 'questionVersions');
  /*
   * NO PAGER AT ALL ON A DRIVEN BLOCK — the earlier answer IS the pointer.
   *
   * `useBlockVersions` now derives both version pointers from the driven answer, so this
   * would be a control that renders a position and cannot change it. Worse than useless: it
   * was the one thing that DID move the question while the recall entry moved only the text,
   * and the two disagreed. Reported from a screenshot: "changing versions changes versions,
   * but clicking the answer link changes nothing ... these need to be united".
   *
   * Read from the block's own identity rather than taken as a prop, for the reason the
   * capability gate above is: seventeen mounts is seventeen places to remember one, and the
   * one that forgot would be a pager quietly fighting the answer again.
   *
   * ON `drivenBy`, NOT on `isDrivenAxis`. The latter is true of an ORPHANED block too —
   * versions still carrying row ids with no driver above them — and there the pointer is
   * live and the pager is the only way to read what is in the bank before collapsing it.
   */
  const scope = useAnswerRefScope();
  const identity = useBlockIdentity();
  const self = identity?.blockId
    ? scope?.tasks.find(tk => tk.id === identity.taskId)?.blocks
      ?.find((b: { id: string }) => b.id === identity.blockId)
    : undefined;
  const driven = !!(self?.data as { drivenBy?: string } | undefined)?.drivenBy;
  /*
   * GATE ADOPTION, NEVER ABANDONMENT — the `+` answers to the capability and the trash does not.
   *
   * Withholding both was the first build and it trapped the teacher: with the capability off, a
   * bank could be read and not dismantled, so turning the feature off meant being stuck with
   * every version and every scenario already authored. Reported plainly — *"i can't delete the
   * scenarios, so i'm essentially stuck with them if i ever turn the feature off ... meanwhile,
   * i can disconnect linked inputs and that's fine"*. The link menu was right and this was not:
   * unlinking a follower has always been one choice away, and a leader had no matching way out.
   *
   * So a capability may hide the way INTO a feature and must never hide the way out. The reverse
   * is what makes a switch a trapdoor, and it is worse than the thing the gate was for: offering
   * a control early merely confuses, while withholding this one destroys the teacher's ability to
   * undo their own work.
   *
   * Safe to leave live because nothing about the delete depends on the gate: it is armed — two
   * clicks, not one — and `removeWarning` names what it would strand before the second lands.
   * And it self-heals, because dropping to one version takes the pager out with it (see `total <
   * 2` below), so a dismantled bank leaves a plain block and no furniture.
   *
   * `projected` still withholds BOTH. A driven axis's rows are the driver's, in the driver's
   * order; deleting one would be `syncDrivenVersions` putting it straight back.
   */
  const onAdd = projected || !mayAuthor ? undefined : onAddRaw;
  const onRemove = projected ? undefined : onRemoveRaw;
  const [armedRaw, setArmedRaw] = useState(false);
  const armed = armedRaw;
  /**
   * WHEN THE ARM SETTLED, so a `pointerleave` caused by the arm itself cannot undo it.
   *
   * Arming can mount a warning beside this button (§7.3). That changes a scrollable panel's
   * height, the browser scrolls to keep the focused button in view, and the button slides out
   * from under a cursor that never moved — firing `pointerleave`, which this control treats as
   * "went away" and disarms on. Confirmed with `getBoundingClientRect` in a previous hunt: the
   * rect moved ~20px between pointerdown and an immediate pointerleave.
   *
   * A real mouse CAN leave within the window and will not disarm, which costs nothing: blur,
   * Escape and a click elsewhere all still disarm, and a button left armed does nothing on its
   * own. See `feedback_arm_hint_layout_shift` for why the alternative — never changing the
   * height — was rejected here: it means permanent empty space for a rare warning.
   */
  const ARM_SETTLE_MS = 300;
  const armedAt = useRef(0);
  const setArmed = useCallback((next: boolean) => {
    if (next) armedAt.current = Date.now();
    setArmedRaw(next);
    onArmedChange?.(next);
  }, [onArmedChange]);
  const disarmFromPointer = useCallback(() => {
    if (Date.now() - armedAt.current < ARM_SETTLE_MS) return;
    setArmed(false);
  }, [setArmed]);

  const index = Math.max(0, versions.findIndex(v => v.id === activeId));
  const total = versions.length;
  const active = versions[index];
  const isLast = index >= total - 1;

  /* See the note above: the answer is the pointer, so there is nothing here to page. */
  if (driven) return null;

  if (total < 2) {
    // Browse-only and nothing to browse: say nothing at all.
    if (!onAdd) return null;
    return (
      <IconButton type='button' size='sm' aria-label={t('blocks.versions.addFirst')} onClick={onAdd}>
        <Plus className='h-4 w-4' />
      </IconButton>
    );
  }

  return (
    /*
     * ARMED HIGHLIGHT, and stronger than the branch carousel's.
     *
     * Same visual language — rounded warning outline over a warning ground, so it is recognisably
     * the app's "this is about to go" — but 2px and a full background rather than 1px and a half
     * one. Deleting a BRANCH removes a condition and its feedback; deleting a VERSION removes a
     * whole question, its answer key, its feedback and any branches inside it. The weight of the
     * warning should match the weight of the loss, and this row sits in a heading where a faint
     * tint reads as a hover state.
     *
     * THE HOUSE ARMED-DELETE, and nothing of its own.
     *
     * `1px` over a half-opacity ground with `-mx-2 px-2 -my-1 py-1`, wrapping the whole row
     * including the trash — which is what twenty other call sites use, every block among them
     * and the branching-correct carousel too. This one had invented 2px over a solid ground
     * with 6px of padding, on the argument that deleting a version costs more than deleting a
     * branch. The argument was fine and the conclusion was not: a louder warning here is just
     * a control that does not look like the app.
     *
     * It is also why the ring would not sit right. An `outline` draws OUTSIDE the box, so the
     * bleed is padding plus outline width, and the heading's gap has to clear that — see the
     * `gap-3` on the dropdown's question heading. Three guesses failed to place it before the
     * numbers were read off a working example.
     *
     * ONE DEVIATION, and only one: `px-1` where the others use `px-2`.
     *
     * Those twenty all sit in a panel body with nothing beside them, so an 8px bleed costs
     * nothing. This pager sits in a HEADING, inches from the scenario link on one side and the
     * panel's own edge on the other, and the negative margin that makes the ring layout-neutral
     * is precisely what makes it land on a neighbour. Measured at 8px: the outline covered the
     * link button. Widening the heading's gap to compensate pushed the trash past the panel
     * edge instead, where flex squeezed the ring and the trash hung out of its right side.
     *
     * `shrink-0` is the other half of that: without it the ring is a flex item that will be
     * compressed below its content, which is what put the trash outside the box it is meant
     * to be inside.
     *
     * NOTE for whoever touches this next: those twenty call sites are twenty copies of one
     * string. There is no shared constant, which is exactly how this one drifted in the first
     * place — it had been 2px over a solid ground.
     */
    /*
     * PAGING IS NOT SELECTING, which is why every button below stops its own click.
     *
     * The pager now rides on a CLOSED scenario leader's card, whose whole surface opens the
     * block — and the one thing a teacher paging a closed card is asking for is to not open it.
     * Each button rather than a wrapper: a div that swallows clicks is a non-interactive element
     * taking an interaction, and these four are the interactive elements already.
     */
    <div className={`flex items-center gap-0.5 ${
      armed ? `shrink-0 rounded-lg ${ARMED_DELETE_PAINT} -mx-1 px-1 -my-1 py-1` : ''
    }`}>
      <IconButton
        type='button'
        size='sm'
        aria-label={t(noun === 'scenario' ? 'blocks.scenario.previous' : 'blocks.versions.previous')}
        disabled={index === 0}
        onClick={e => { e.stopPropagation(); const prev = versions[index - 1]; if (prev) onSelect(prev.id); }}
      >
        <ChevronLeft className='h-4 w-4' />
      </IconButton>
      {/* `aria-live`, like the carousel's: paging IS the interaction, and a reader who cannot see
          the question change needs to hear which one they landed on. */}
      <span className='shrink-0 px-0.5 text-xs font-medium tabular-nums text-ws-muted' aria-live='polite'>
        {t(!showNoun ? 'blocks.versions.positionShort'
          : noun === 'scenario' ? 'blocks.versions.positionScenario'
            : 'blocks.versions.position', { current: index + 1, total })}
      </span>
      {/*
        * AT THE END, THE `+` ASKS BEFORE IT ADDS.
        *
        * The hazard the owner reported, in their words: paging quickly and ending up with six
        * new scenarios. It is not really about speed — it is that ONE BUTTON does two
        * different things depending on where you are, and you cannot see which until you have
        * pressed it. A menu makes the last press non-destructive: mindless paging ends on
        * something you can dismiss, and nothing was created.
        *
        * ONE ITEM, deliberately. Adding already copies the variant you are on
        * (`addDisplayVersion` reads `editorVersionId`), so "New" and "Duplicate" would be the
        * same act wearing two labels — the task stepper's menu has two because there they
        * genuinely differ. The menu earns its place by what it PREVENTS, not by what it offers.
        */}
      {isLast && onAdd ? (
        <FlowPickerMenu
          sections={[{ options: [{ key: 'add', text: t(noun === 'scenario' ? 'blocks.scenario.addOne' : 'blocks.versions.addOne') }] }]}
          onChoose={() => onAdd()}
          ariaLabel={t('blocks.versions.addAnother')}
          /* No filter over a single option — see `FlowPickerMenu`'s own note. */
          noMatchesLabel={t('blocks.versions.addAnother')}
          renderTrigger={p => (
            <IconButton
              ref={p.ref}
              type='button'
              size='sm'
              onClick={e => { e.stopPropagation(); p.onClick(); }}
              aria-haspopup={p['aria-haspopup']}
              aria-expanded={p['aria-expanded']}
              aria-label={t('blocks.versions.addAnother')}
            >
              <Plus className='h-4 w-4' />
            </IconButton>
          )}
        />
      ) : (
      <IconButton
        type='button'
        size='sm'
        /* Browsing, the last step is simply the last step. */
        disabled={isLast}
        aria-label={t(noun === 'scenario' ? 'blocks.scenario.next' : 'blocks.versions.next')}
        onClick={e => {
          e.stopPropagation();
          const next = versions[index + 1];
          if (next) onSelect(next.id);
        }}
      >
        <ChevronRight className='h-4 w-4' />
      </IconButton>
      )}
      {onRemove && (
        <IconButton
          type='button'
          variant='destructive'
          size='sm'
          isArmed={armed}
          /* Never disabled: removing the second-to-last version collapses the block back to a plain
             question rather than refusing, which is what makes trying a bank reversible. */
          aria-label={armed ? t('blocks.versions.confirmRemove') : t('blocks.versions.remove')}
          onClick={e => {
            e.stopPropagation();
            if (!active) return;
            if (armed) { onRemove(active.id); setArmed(false); } else setArmed(true);
          }}
          onPointerLeave={e => { if (e.pointerType !== 'touch' && armed) disarmFromPointer(); }}
          onBlur={() => { if (armed) setArmed(false); }}
          onKeyDown={e => { if (e.key === 'Escape' && armed) { e.stopPropagation(); setArmed(false); } }}
        >
          <Trash2 className='h-4 w-4' />
        </IconButton>
      )}
    </div>
  );
}
